import { Check } from '@untitled-ui/icons-react/build/cjs';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import CardCmp from 'src/components/CardCmp';
import OutsideClickHandler from 'src/components/OutsideClickHandler';
import TableCmp from 'src/components/TableCmp';
import TooltipCmp from 'src/components/TooltipCmp';
import TruncateTextCmp from 'src/components/TruncateText';
import { useAppSelector } from 'src/redux/hooks';
import { generateUnitSummary } from 'src/utils/CommonFunctions';

import { useRadarData } from '../../hooks/useRadarData';

import { SHARE_MOBILE_CARDCMP, SHARE_MOBILE_ROW_TEXT } from './common/shareMobile.styles';

const LINE_ITEM_HEAD_CELLS = [
    { id: 'handlingUnit', name: 'Handling Unit' },
    { id: 'no', name: '№' },
    { id: 'dimensions', name: 'Dimensions' },
    { id: 'totalWeight', name: 'Total Weight' },
    { id: 'fc', name: 'FC' },
];

const LineItemRow = ({ data }: { data: any }) => {
    const weightUnit = data?.weightMeasurement === 'KGS' ? 'kgs' : 'lbs';

    return (
        <>
            <td className="px-3 py-2">{data?.handlingUnitLabel || '-'}</td>
            <td className="px-3 py-2">{data?.handlingUnitNo || '-'}</td>
            <td className="px-3 py-2">{data?.length && data?.width && data?.height ? `${data.length} x ${data.width} x ${data.height}` : '-'}</td>
            <td className="px-3 py-2">{data?.totalWeight ? `${data.totalWeight} ${weightUnit}` : '-'}</td>
            <td className="px-3 py-2">{data?.freightClass || '-'}</td>
        </>
    );
};

// A row with no data resolves to this. Rows that resolve to it are dropped entirely rather than
// rendered as "Label   -", so the card only ever lists details the order actually has.
const EMPTY_VALUE = '-';

const hasValue = (row: { value: string }) => row.value !== EMPTY_VALUE;

interface IProps {
    // Load Insurance ID and Quote ID are internal references the customer has no
    // use for on a public share link, so those rows are hidden there.
    hideInternalReferenceRows?: boolean;
    // Share-link mobile sheet (Order tab) — Figma card styling, same rows + interactions.
    isShareMobile?: boolean;
}

const OrderDetailsSection: React.FC<IProps> = ({ hideInternalReferenceRows = false, isShareMobile = false }) => {
    // Figma share-mobile rows: label + value in text-tertiary, 8px apart.
    const rowTextClass = isShareMobile ? SHARE_MOBILE_ROW_TEXT : '';
    const rowGapClass = isShareMobile ? 'gap-y-2' : 'gap-y-1.5';
    const { selectedLeg, isDetailLoading, trackOrderDetail } = useRadarData();
    const handlingUnitOptions = useAppSelector((state: any) => state.basic.handlingUnitOptions);
    const lineItemsSummaryRef = useRef<HTMLSpanElement>(null);

    const [isLineItemsOpen, setIsLineItemsOpen] = useState(false);
    // Share sheet: line items open inline under the row (tap), instead of the hover popover.
    const [isLineItemsInlineOpen, setIsLineItemsInlineOpen] = useState(false);
    const [openUpward, setOpenUpward] = useState(false);
    const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
    const lineItemsTriggerRef = useRef<HTMLDivElement>(null);
    const lineItemsCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(
        () => () => {
            if (lineItemsCloseTimerRef.current) clearTimeout(lineItemsCloseTimerRef.current);
        },
        []
    );

    const isFTL = (selectedLeg?.orderType ?? trackOrderDetail?.orderType)?.toLowerCase() === 'ftl';

    const dimensions = useMemo(() => trackOrderDetail?.dimensions ?? [], [trackOrderDetail]);

    const dimensionsWithLabel = useMemo(
        () =>
            dimensions.map((dim: any) => ({
                ...dim,
                handlingUnitLabel: handlingUnitOptions?.find((item: any) => item?.value === dim?.handlingUnit)?.label,
                unit: handlingUnitOptions?.find((item: any) => item?.value === dim?.handlingUnit)?.label?.toLowerCase(),
            })),
        [dimensions, handlingUnitOptions]
    );

    const cancelCloseLineItems = () => {
        if (lineItemsCloseTimerRef.current) {
            clearTimeout(lineItemsCloseTimerRef.current);
            lineItemsCloseTimerRef.current = null;
        }
    };

    const openLineItems = () => {
        cancelCloseLineItems();

        if (lineItemsTriggerRef.current) {
            const rect = lineItemsTriggerRef.current.getBoundingClientRect();
            // Dropdown height: header + table rows, capped by the table's 200px max-height
            const estimatedHeight = Math.min(80 + dimensionsWithLabel.length * 33, 280);
            const spaceBelow = window.innerHeight - rect.bottom;
            const goUp = spaceBelow < estimatedHeight + 12 && rect.top > spaceBelow;
            setOpenUpward(goUp);
            // Left edge aligned with the "Line Items" label, kept 8px inside the viewport.
            // Popover = 410px table + 24px padding. position:fixed → viewport coords, no scroll offsets.
            const popoverWidth = 434;
            const left = Math.max(8, Math.min(rect.left, window.innerWidth - popoverWidth - 8));
            setDropdownPos({
                top: goUp ? rect.top : rect.bottom,
                left,
            });
        }
        setIsLineItemsOpen(true);
    };

    const scheduleCloseLineItems = () => {
        cancelCloseLineItems();
        lineItemsCloseTimerRef.current = setTimeout(() => setIsLineItemsOpen(false), 150);
    };

    const lineItemsSummary = useMemo(
        () => (dimensionsWithLabel.length > 0 ? generateUnitSummary(dimensionsWithLabel, ', ') || `${dimensionsWithLabel.length} item${dimensionsWithLabel.length !== 1 ? 's' : ''}` : '-'),
        [dimensionsWithLabel]
    );

    const additionalServices = useMemo(() => trackOrderDetail?.additionalServices ?? [], [trackOrderDetail]);
    const pickupServices = useMemo(() => additionalServices.filter((s: any) => s?.serviceCategory === 'PICKUP' || s?.serviceType === 'PICKUP'), [additionalServices]);
    const deliveryServices = useMemo(() => additionalServices.filter((s: any) => s?.serviceCategory === 'DELIVERY' || s?.serviceType === 'DELIVERY'), [additionalServices]);
    const otherServices = useMemo(() => additionalServices.filter((s: any) => s?.serviceCategory === 'OTHER' || s?.serviceType === 'OTHER'), [additionalServices]);

    const primaryRows = useMemo(
        () => [
            { key: 'totalWeight', label: 'Total Weight', value: selectedLeg?.totalWeight ? `${selectedLeg.totalWeight} lbs` : '-' },
            { key: 'linearFootage', label: 'Linear Footage', value: selectedLeg?.linearFootage ? `${selectedLeg.linearFootage} ft` : '-' },
        ],
        [selectedLeg]
    );

    const collapsibleRows = useMemo(
        () => [
            { key: 'refNumber', label: 'REF Number', value: selectedLeg?.refNumber || '-' },
            { key: 'poNumber', label: 'PO Number', value: selectedLeg?.poNumber || '-' },
            ...(hideInternalReferenceRows ? [] : [{ key: 'loadInsuranceId', label: 'Load Insurance ID', value: trackOrderDetail?.loadInsuranceId ? `#${trackOrderDetail.loadInsuranceId}` : '-' }]),
            { key: 'pickupNumber', label: 'Pickup Number', value: selectedLeg?.pickupNumber || '-' },
            { key: 'deliveryNumber', label: 'Delivery Number', value: selectedLeg?.deliveryNumber || '-' },
            ...(hideInternalReferenceRows ? [] : [{ key: 'quoteId', label: 'Quote ID', value: selectedLeg?.quoteCode ? `#${selectedLeg.quoteCode}` : '-' }]),
        ],
        [selectedLeg, trackOrderDetail, hideInternalReferenceRows]
    );

    const serviceRows = useMemo(
        () => [
            { key: 'pickupAdditionalServices', label: 'Pickup Additional Services', services: pickupServices },
            { key: 'deliveryAdditionalServices', label: 'Delivery Additional Services', services: deliveryServices },
            { key: 'otherAdditionalServices', label: 'Other Additional Services', services: otherServices },
        ],
        [pickupServices, deliveryServices, otherServices]
    );

    // Only rows that carry a value are rendered. The unfiltered arrays above are still used by the
    // loading skeleton, where no value is known yet and every label must show as a placeholder.
    const visiblePrimaryRows = useMemo(() => primaryRows.filter(hasValue), [primaryRows]);
    const visibleCollapsibleRows = useMemo(() => collapsibleRows.filter(hasValue), [collapsibleRows]);
    const visibleServiceRows = useMemo(() => serviceRows.filter(({ services }) => services.length > 0), [serviceRows]);
    // Chevron only when there is something to collapse — with just the three always-visible rows it
    // toggled an empty section (only its top padding), which read as a flicker. Loading keeps it so
    // the skeleton header doesn't change.
    const hasCollapsibleRows = isDetailLoading || visibleCollapsibleRows.length > 0 || visibleServiceRows.length > 0;

    const renderLoadingRow = (key: string, label: string) => (
        <div key={key} className="flex items-center justify-between gap-x-4">
            <span className={`text-xs leading-[18px] text-textSecondary flex-none ${rowTextClass}`}>{label}</span>
            <span className="custom-loading text-xs w-20">Loading</span>
        </div>
    );

    const renderRow = ({ key, label, value }: { key: string; label: string; value: string }) => (
        <div key={key} className="flex items-center justify-between gap-x-4">
            <span className={`text-xs leading-[18px] text-textSecondary flex-none ${rowTextClass}`}>{label}</span>
            {value === '-' ? (
                <span className={`text-xs font-medium leading-[18px] text-textSecondary text-right ${rowTextClass}`}>{value}</span>
            ) : (
                <TooltipCmp message={value} parentClassName="max-w-[300px] w-max" childrenClassName="min-w-0 flex justify-end">
                    <span className={`text-xs font-medium leading-[18px] text-textSecondary text-right truncate block max-w-[220px] cursor-pointer ${rowTextClass}`}>{value}</span>
                </TooltipCmp>
            )}
        </div>
    );

    const renderServiceRow = ({ key, label, services }: { key: string; label: string; services: any[] }) => {
        const count = services.length;

        return (
            <div key={key} className="flex items-center justify-between gap-x-4">
                {count > 0 ? (
                    <TooltipCmp
                        position="bottom"
                        // Hug the content (was a fixed 350px min width, leaving a wide empty right side).
                        parentClassName="text-left max-w-[350px]"
                        childrenClassName="!flex min-w-0"
                        message={
                            <div className="py-1.5 px-2">
                                <p className="text-xs font-medium text-grayLight900 mb-1.5">{label}</p>
                                <div className="grid grid-cols-2 gap-x-4 gap-y-1 overflow-hidden">
                                    {services.map((service: any, index: number) => (
                                        <div key={index} className="flex items-center gap-x-1.5 text-left overflow-hidden">
                                            <Check className="w-3.5 h-3.5 text-primary flex-none" />
                                            <span className="text-xs text-textSecondary truncate">{service?.serviceName || '-'}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        }
                    >
                        <span className={`text-xs leading-[18px] text-textSecondary flex-none border-b border-dashed border-utilityGray200 cursor-pointer ${rowTextClass}`}>{label}</span>
                    </TooltipCmp>
                ) : (
                    <span className={`text-xs leading-[18px] text-textSecondary flex-none ${rowTextClass}`}>{label}</span>
                )}
                <span className={`text-xs font-medium leading-[18px] text-textSecondary text-right ${rowTextClass}`}>{count > 0 ? count : '-'}</span>
            </div>
        );
    };

    return (
        <CardCmp
            headerTitle="Order Details"
            isCardContentWithoutSpacing={false}
            isAccordion={hasCollapsibleRows}
            isFooter={false}
            // Partial accordion, like the wal-frontend-v2 radar card: Total Weight / Linear Footage /
            // Line Items stay visible when collapsed, everything else sits in .thisSectionAccordion.
            specificSectionAccordion={hasCollapsibleRows}
            headerParentClassName={`!border-b-0 px-4 radar-card-title ${isShareMobile ? SHARE_MOBILE_CARDCMP.headerParentClassName : ''}`}
            cardClassName={isShareMobile ? SHARE_MOBILE_CARDCMP.cardClassName : '!border-0 !rounded-lg'}
            childrenClassName={`px-4 pb-3 ${isShareMobile ? SHARE_MOBILE_CARDCMP.childrenClassName : ''}`}
        >
            {isDetailLoading ? (
                <div className="flex flex-col">
                    <div className="flex flex-col gap-y-2">
                        {primaryRows.map(({ key, label }) => renderLoadingRow(key, label))}
                        {!isFTL && renderLoadingRow('lineItems', 'Line Items')}
                    </div>
                    <div className="thisSectionAccordion">
                        <div className="overflow-hidden">
                            <div className="flex flex-col gap-y-2 pt-2">{[...collapsibleRows, ...serviceRows].map(({ key, label }) => renderLoadingRow(key, label))}</div>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="flex flex-col">
                    <div className={`flex flex-col ${rowGapClass}`}>
                        {visiblePrimaryRows.map(renderRow)}

                        {/* Line Items — always visible with Total Weight / Linear Footage; hidden for FTL
                            orders, and hidden when the order has no line items at all */}
                        {!isFTL && dimensionsWithLabel.length > 0 && isShareMobile && (
                            <div className="flex flex-col">
                                <div className="flex items-center justify-between gap-x-4 cursor-pointer" onClick={() => setIsLineItemsInlineOpen((prev) => !prev)}>
                                    <p className={`text-xs leading-[18px] text-textSecondary text-nowrap border-b border-dashed border-utilityGray200 ${rowTextClass}`}>Line Items</p>
                                    <span className={`text-xs font-medium leading-[18px] text-textSecondary text-right truncate min-w-0 ${rowTextClass}`}>{lineItemsSummary}</span>
                                </div>
                                {/* Full-width inline table, same grid-rows slide as CardCmp */}
                                <div style={{ display: 'grid', gridTemplateRows: isLineItemsInlineOpen ? '1fr' : '0fr', transition: 'grid-template-rows 300ms ease' }}>
                                    <div className="overflow-hidden">
                                        {/* Same table + headers as the desktop popover; scrolls sideways if the columns don't fit */}
                                        <div className="mt-2 overflow-x-auto custom-scrollbar-v2 table-without-pagination table-box-wrap !rounded-lg !shadow-xs">
                                            <TableCmp headCells={LINE_ITEM_HEAD_CELLS} tableDataArr={dimensionsWithLabel} TableRowCmp={LineItemRow} tableRowHeight="xs" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                        {!isFTL && dimensionsWithLabel.length > 0 && !isShareMobile && (
                            <div className="flex items-center justify-between gap-x-4">
                                <OutsideClickHandler onOutsideClick={() => setIsLineItemsOpen(false)} containerClassName="inline-block">
                                    <div
                                        ref={lineItemsTriggerRef}
                                        className="inline-block !flex"
                                        onMouseEnter={() => {
                                            if (dimensionsWithLabel.length > 0) openLineItems();
                                        }}
                                        onMouseLeave={scheduleCloseLineItems}
                                    >
                                        <p
                                            className={`text-xs leading-[18px] text-textSecondary text-nowrap ${rowTextClass} ${
                                                dimensionsWithLabel.length > 0 ? 'border-b border-dashed border-utilityGray200 cursor-pointer' : 'cursor-default'
                                            }`}
                                        >
                                            Line Items
                                        </p>
                                    </div>
                                    {isLineItemsOpen &&
                                        dimensionsWithLabel.length > 0 &&
                                        ReactDOM.createPortal(
                                            <div
                                                style={{
                                                    position: 'fixed',
                                                    top: openUpward ? undefined : dropdownPos.top,
                                                    bottom: openUpward ? window.innerHeight - dropdownPos.top + 6 : undefined,
                                                    left: dropdownPos.left,
                                                    marginTop: openUpward ? undefined : 6,
                                                }}
                                                className="z-[9999] bg-white border border-utilityGray200 rounded-lg shadow px-3 py-2"
                                                onMouseEnter={cancelCloseLineItems}
                                                onMouseLeave={scheduleCloseLineItems}
                                            >
                                                <p className="text-[12px] leading-[18px] font-medium text-grayLight900 mb-2 text-left flex">Line Items</p>
                                                <div className="overflow-x-auto overflow-y-auto max-h-[206px] custom-scrollbar-v2 table-without-pagination table-box-wrap max-w-[410px] w-[410px] !shadow">
                                                    <TableCmp headCells={LINE_ITEM_HEAD_CELLS} tableDataArr={dimensionsWithLabel} TableRowCmp={LineItemRow} tableRowHeight="xs" />
                                                </div>
                                            </div>,
                                            document.body
                                        )}
                                </OutsideClickHandler>
                                <TruncateTextCmp message={lineItemsSummary} refName={lineItemsSummaryRef}>
                                    <span ref={lineItemsSummaryRef} className={`text-xs font-medium leading-[18px] text-textSecondary text-right truncate block max-w-[220px] ${rowTextClass}`}>
                                        {lineItemsSummary}
                                    </span>
                                </TruncateTextCmp>
                            </div>
                        )}
                    </div>

                    {/* Collapsed by the chevron; the three rows above stay visible either way */}
                    {hasCollapsibleRows && (
                        <div className="thisSectionAccordion">
                            <div className="overflow-hidden">
                                <div className={`flex flex-col ${rowGapClass} ${isShareMobile ? 'pt-2' : 'pt-1.5'}`}>
                                    {visibleCollapsibleRows.map(renderRow)}
                                    {visibleServiceRows.map(renderServiceRow)}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </CardCmp>
    );
};

export default OrderDetailsSection;
